import { Check, Close, CloudUpload, Download, Search, Mail } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import get from "lodash/get";
import { JSXElementConstructor, ReactElement, ReactNode, ReactPortal, useCallback, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import contractService from "../../services/contract";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No contracts found
    </Typography>
  </Box>
);

// Function to get status chip based on contract status
const getStatusChip = (status: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined) => {
  let color: 'success' | 'warning' | 'info' | 'primary' | 'error' | 'default' | 'secondary';
  let displayLabel = String(status);

  switch (status) {
    case 'active':
      color = 'success';
      break;
    case 'draft':
      color = 'secondary';
      break;
    case 'employee_review':
      color = 'warning';
      break;
    case 'hr_review':
      color = 'info';
      break;
    case 'pending_upload':
      color = 'warning';
      break;
    case 'pending_hr_upload':
      color = 'warning';
      break;
    case 'completed':
      color = 'primary';
      break;
    case 'cancelled':
    case 'rejected':
      color = 'error';
      break;
    default:
      color = 'default';
  }

  // Format display label to be more user-friendly
  displayLabel = displayLabel.replace(/_/g, ' ').split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return <Chip label={displayLabel} color={color} size="small" />;
};

export default function ContractList() {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState<number>(10);
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [contractToReject, setContractToReject] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  const notification = useNotification();
  const alert = useAlert();

  const listContracts = useQuery(
    ["contractList", options],
    ({ queryKey }) => contractService.getAllContracts(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const downloadContract = useMutation(contractService.downloadContract, {
    onSuccess: () => {
      notification.onOpen({
        message: "Contract document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download contract",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  // HR approve contract mutation
  const approveContract = useMutation(
    (contractId: number) => contractService.hrApproveContract({
      id: contractId,
      hr_id: user.id,
      last_updated_by: user.name || user.username,
      role_id: user.role_id
    }),
    {
      onSuccess: () => {
        listContracts.refetch();
        notification.onOpen({
          message: "Contract approved successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to approve contract",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  // HR reject contract mutation
  const rejectContract = useMutation(
    (params: { id: number, rejection_reason: string }) => contractService.hrRejectContract({
      id: params.id,
      hr_id: user.id,
      rejection_reason: params.rejection_reason,
      last_updated_by: user.name || user.username,
      role_id: user.role_id
    }),
    {
      onSuccess: () => {
        listContracts.refetch();
        notification.onOpen({
          message: "Contract rejected successfully!",
          type: "success",
          position: "top",
        });
        setRejectDialogOpen(false);
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to reject contract",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  // HR upload document mutation
  const uploadHrDocument = useMutation(
    (payload: { id: number; file: File }) =>
      contractService.uploadHrDocument(payload.id, payload.file),
    {
      onSuccess: () => {
        setUploadingId(null);
        listContracts.refetch();
        notification.onOpen({
          message: "HR document uploaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        setUploadingId(null);
        notification.onOpen({
          message: "Failed to upload HR document",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const handleDownload = (contractId: number) => {
    downloadContract.mutate(contractId);
  };

  const handleApproveContract = (contractId: number) => {
    alert.onOpen({
      title: "Confirm Approval",
      message: "Are you sure you want to approve this contract?",
      actions: [
        {
          color: "success",
          variant: "contained",
          children: "Approve",
          onClick: () => {
            approveContract.mutate(contractId);
            alert.onClose();
          },
        },
        {
          color: "inherit",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const handleRejectContract = (contractId: number) => {
    setContractToReject(contractId);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const submitRejection = () => {
    if (contractToReject !== null) {
      rejectContract.mutate({
        id: contractToReject,
        rejection_reason: rejectionReason || "No reason provided"
      });
    }
  };

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: string }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  // Check if contract can be approved/rejected (only hr_review status)
  const canManageContract = (status: string) => {
    return status === 'hr_review';
  };

  const handleUploadClick = (contractId: number) => {
    setUploadingId(contractId);
    fileInputRef.current?.click();
  };

  const handleResendReviewLink = (contractId: number) => {
    alert.onOpen({
      title: "Confirm Resend",
      message: "Are you sure you want to resend the review link to the employee?",
      actions: [
        {
          color: "primary",
          variant: "contained",
          children: "Resend",
          onClick: () => {
            resendReviewLink.mutate(contractId);
            alert.onClose();
          },
        },
        {
          color: "inherit",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    contractId: number
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadHrDocument.mutate({ id: contractId, file });
    }
    // Reset file input value so the same file can be selected again if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadBulk = useMutation("upload_bulk", contractService.uploadBulk, {
    onSuccess: () => window.location.reload()
  });

  // Resend review link mutation
  const resendReviewLink = useMutation(contractService.resendReviewLink, {
    onSuccess: () => {
      listContracts.refetch();
      notification.onOpen({
        message: "Review link sent successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to send review link",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleUploadBulk = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append("officer_name", user.username);
    formData.append("officer_id", user.id);
    event.target.files && formData.append("file", event.target.files[0]);
    uploadBulk.mutate(formData);
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Contract Management</title>
      </Helmet>

      <Breadcrumb
        label="Contract Management"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      {/* Only HR can access this page */}
      <ProtectedPermissionPage acceptPermissions={[1]}>
        {/* Hidden file input for HR document upload */}
        <input
          type="file"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={(e) => {
            if (uploadingId) handleFileChange(e, uploadingId);
          }}
          accept="application/pdf"
          data-testid="upload-hr-document-input"
        />

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <Controller
                name="keyword"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search contracts..."
                    {...field}
                    {...config.onEnter(() => {
                      form.handleSubmit(onHandleSearch)();
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={form.handleSubmit(onHandleSearch)}
                        >
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <LoadingButton variant="contained" component="label" loading={uploadBulk.isLoading}>
                Upload Bulk Contract <input onChange={handleUploadBulk} type="file" hidden />
              </LoadingButton>
              <Button variant="outlined" onClick={() => contractService.getBulkTemplate()}>
                Bulk Contract Template
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/contract/create-contract"
              >
                Create Contract
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listContracts.isLoading}
            getRowId={(record) => record.id}
            rows={get(listContracts, "data.contracts", [])}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "created_time",
                headerName: "Created Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {dayjs(params.value).format("DD MMM YYYY HH:mm:ss")}
                  </Typography>
                ),
              },
              {
                field: "name",
                headerName: "Employee Name",
                flex: 1,
                minWidth: 180,
              },
              {
                field: "contract_end",
                headerName: "Contract End Date",
                flex: 1,
                minWidth: 130,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                minWidth: 130,
                renderCell: (params) => getStatusChip(params.value),
              },
              {
                field: "id",
                headerName: "Actions",
                sortable: false,
                flex: 1,
                minWidth: 220,
                renderCell: (params) => {
                  const contract = params.row;

                  return (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Download contract">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(params.value)}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>

                      {/* Details button for all contracts */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/app/contract/detail/${params.value}`)}
                      >
                        Details
                      </Button>

                      {/* Edit button - Only shown for contracts with draft status */}
                      {contract.status === 'draft' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          onClick={() => navigate(`/app/contract/edit/${params.value}`)}
                        >
                          Edit
                        </Button>
                      )}

                      {/* Approve/Reject buttons - Only shown for contracts in HR review */}
                      {canManageContract(contract.status) && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveContract(params.value)}
                            >
                              <Check />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectContract(params.value)}
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* Upload HR document button - Only shown for contracts with pending_hr_upload status */}
                      {contract.status === 'pending_hr_upload' && (
                        <Tooltip title="Upload HR Document">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleUploadClick(params.value)}
                            disabled={uploadHrDocument.isLoading && uploadingId === params.value}
                          >
                            <CloudUpload />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Resend review link button - Only shown for contracts in employee_review status */}
                      {contract.status === 'employee_review' && (
                        <Tooltip title="Resend Review Link">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleResendReviewLink(params.value)}
                            disabled={resendReviewLink.isLoading}
                          >
                            <Mail />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-columnHeaderTitleContainer": {
                backgroundColor: "#22404a",
                color: "white",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#22404a",
                color: "white",
              },
              ".MuiDataGrid-columnSeparator": {
                backgroundColor: "#22404a",
                color: "white",
              },
            }}
          />
        </Box>

        {/* Custom Dialog for Rejection Reason */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Contract</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please provide a reason for rejecting this contract:
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={submitRejection} color="error" variant="contained">
              Reject Contract
            </Button>
          </DialogActions>
        </Dialog>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}